<script>
    const preventDefault = e => {
        e.preventDefault();
    };

    /**
     * A tab within an [ib-tab-bar](#/components/layout/ib-tab-bar).
     */
    export default {
        name: 'ib-tab',

        props: {
            /**
             * Semantic color class; applies to underbar with `underline` appearance or
             * text with `clear` appearance.
             */
            tone: String,
            /**
             * Alignment (`start` or `end`); if it differs from the bar alignment, this tab
             * will be separated from the others.
             */
            align: String,
            /**
             * If true, grey out this tab and make it inert.
             */
            disabled: Boolean,
            /**
             * The URL location associated with this tab, if any.
             */
            href: String,
            /**
             * The destination route associated with this tab, if any.
             */
            to: [Object,String],
            /**
             * If a route is specified, this tab will become active if any of its child routes are
             * navigated to, unless this property is `true`.
             */
            exact: Boolean
        },

        data() {
            return {
                active: false
            };
        },

        watch: {
            tone: 'sync',
            disabled: 'sync'
        },

        created() {
            let tabs = this.$parent;
            while (tabs) {
                if (tabs.isTabs) {
                    tabs.sync();
                    this.tabs = tabs;
                    return;
                }
                tabs = tabs.$parent;
            }
            throw new Error('no scope');
        },

        destroyed() {
            this.sync();
        },

        render(h) {
            const vm = this;
            const to = vm.to;
            const disabled = vm.disabled;

            const render = href => h('a', {
                staticClass: '_tab',
                'class': [
                    vm.active ? vm.tone ? '_taba-' + vm.tone : '_taba' : void 0,
                    vm.align ? '_a' + vm.align : void 0
                ],
                attrs: {
                    'href': disabled ? void 0 : href,
                    'role': 'tab',
                    'tabindex': href
                        ? disabled ? -1 : void 0
                        : disabled ? void 0 : 0,
                    'disabled': disabled,
                    'aria-selected': String(vm.active)
                },
                on: href ? {
                    dragstart: preventDefault
                } : {
                    click: vm.click
                }
            }, [
                vm._t('default'),
                ...vm.tabs.appearance == 'clear' ? [
                    h('div', {
                        'staticClass': '_tabcph'
                    }, [
                        vm._t('default')
                    ])
                ] : []
            ]);

            if (to !== void 0) {
                return h('router-link', {
                    attrs: {
                        to,
                        exact: vm.exact,
                        custom: ''
                    },
                    scopedSlots: vm._u([{
                        key: 'default',
                        fn: ({ href, isActive }) => {
                            if (isActive && !vm.wasActive) {
                                vm.tabs.select(vm._self, true);
                            }
                            vm.wasActive = isActive;
                            return [
                                render(href)
                            ];
                        }
                    }], null, true)
                });
            } else {
                return render(vm.href);
            }
        },

        methods: {
            sync() {
                if (this.tabs.sync) {
                    this.tabs.sync();
                }
            },

            click() {
                if (!this.disabled) {
                    this.tabs.select(this, true);
                }
            },

            enter() {
                this.active = true;
                this.$emit('enter');
            },

            leave() {
                this.active = false;
                this.$emit('leave');
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tab {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        -webkit-tap-highlight-color: transparent;
        border-bottom: @tab-border solid transparent;
        margin: @tab-margin;
        padding: @tab-padding;
        white-space: nowrap;
        color: inherit !important;
        cursor: pointer;
        opacity: .42;
        transition: opacity .2s;
        outline-offset: -2px;
        line-height: 1;
        .tab-default();

        &:hover when (@have-hover) {
            opacity: .7;
        }
        &[disabled] {
            opacity: .15;
            cursor: default;
        }
        &[aria-selected=true] {
            opacity: 1;
            &[disabled] {
                opacity: .5;
            }
        }
        &._astart {
            -webkit-margin-end: auto;
            margin-inline-end: auto;
        }
        &._aend {
            -webkit-margin-start: auto;
            margin-inline-start: auto;
        }
    }

    ._tabs2 {
        &._eclear ._tab {
            border-bottom: none;
            &:first-child {
                margin-inline-start: 0;
                padding-inline-start: 0;
            }
            &:last-child {
                margin-inline-end: 0;
                padding-inline-end: 0;
            }
            &[aria-selected=true] {
                font-weight: 600;
            }
        }
        &._scompact ._tab {
            & when not (@tab-margin-compact = none) {
                margin: @tab-margin-compact;
            }
            & when not (@tab-padding-compact = none) {
                padding: @tab-padding-compact;
            }
        }
        &._scomfortable ._tab {
            & when not (@tab-margin-comfort = none) {
                margin: @tab-margin-comfort;
            }
            & when not (@tab-padding-comfort = none) {
                padding: @tab-padding-comfort;
            }
        }
    }

    ._tabcph {
        visibility: hidden;
        height: 0;
        font-weight: 600;
    }
</style>
